import React, { useState, useEffect } from 'react';
import { X, UserPlus, UserMinus, Crown, Search, Users, Trash2 } from 'lucide-react';
import ProfileImage from '../common/ProfileImage';

const ConversationMembersModal = ({ 
  isOpen, 
  onClose, 
  conversation, 
  currentUser, 
  availableUsers, 
  onAddParticipant, 
  onRemoveParticipant,
  onDeleteConversation 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredAvailableUsers, setFilteredAvailableUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removeUser, setRemoveUser] = useState(null);

  useEffect(() => {
    if (!availableUsers || !conversation) return;
    
    const participantIds = conversation.participants?.map(p => p._id) || [];
    const creatorId = conversation.creator?._id;
    
    const available = availableUsers.filter(user => 
      !participantIds.includes(user._id) &&
      user._id !== currentUser.id &&
      user._id !== creatorId  // Prevent creator from being added again
    );
    
    const filtered = searchQuery 
      ? available.filter(user =>
          `${user.name} ${user.lastName} ${user.email}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        )
      : available;
    
    setFilteredAvailableUsers(filtered);
  }, [availableUsers, conversation, searchQuery, currentUser]);

  // Utility function for safe ID comparison (same as ChatArea)
  const safeIdCompare = React.useCallback((id1, id2) => {
    if (!id1 || !id2) return false;
    const str1 = typeof id1 === 'object' ? id1.toString() : String(id1);
    const str2 = typeof id2 === 'object' ? id2.toString() : String(id2);
    return str1 === str2;
  }, []);

  if (!isOpen || !conversation) {
    return null;
  }

  // Validate required data
  if (!currentUser) {
    return null;
  }

  // Robust permission checking with validation
  const creatorId = conversation.creator?._id;
  const userId = currentUser?.id;
  const userRole = currentUser?.role;

  // Validate required IDs
  if (!creatorId || !userId) {
    // Default to view-only mode if data is incomplete
    const canManageMembers = userRole === 'admin'; // Only admin can manage if creator data is missing
    const isCreator = false;
    const isAdmin = userRole === 'admin';


    // Continue with modal rendering using fallback permissions
    // We'll use the same JSX structure but with fallback permissions
  }
  
  const isCreator = safeIdCompare(creatorId, userId);
  const isAdmin = userRole === 'admin';
  const canManageMembers = isCreator || isAdmin;
  

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'instructor':
        return 'bg-yellow-100 text-yellow-800';
      case 'student':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleAddParticipant = async (user) => {

    if (!canManageMembers) {
      alert('You do not have permission to add participants to this conversation.');
      return;
    }

    // Validate required data
    if (!user?._id || !conversation?.id) {
      alert('Unable to add participant due to missing data.');
      return;
    }
    
    // Additional frontend validation
    if (safeIdCompare(user._id, conversation.creator?._id)) {
      alert('Cannot add the conversation creator as a participant.');
      return;
    }
    
    if (conversation.participants?.some(p => safeIdCompare(p._id, user._id))) {
      alert('This user is already a participant in the conversation.');
      return;
    }
    
    setIsLoading(true);
    try {
      await onAddParticipant(conversation.id, user._id);
    } catch (error) {
      alert(`Failed to add participant: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveParticipant = async (user) => {

    if (!canManageMembers) {
      alert('You do not have permission to remove participants from this conversation.');
      return;
    }

    // Validate required data
    if (!user?._id || !conversation?.id) {
      alert('Unable to remove participant due to missing data.');
      return;
    }

    if (safeIdCompare(user._id, conversation.creator?._id)) {
      alert('Cannot remove the conversation creator.');
      return;
    }

    setRemoveUser(user);
    setShowRemoveConfirm(true);
  };

  const confirmRemoveParticipant = async () => {
    if (!removeUser) return;
    setShowRemoveConfirm(false);
    setIsLoading(true);
    try {
      await onRemoveParticipant(conversation.id, removeUser._id);
    } catch (error) {
      alert(`Failed to remove participant: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
      setRemoveUser(null);
    }
  };

  const handleDeleteConversation = async () => {

    if (!canManageMembers) {
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDeleteConversation = async () => {
    setShowDeleteConfirm(false);
    setIsLoading(true);
    try {
      await onDeleteConversation(conversation.id);
      onClose();
    } catch (error) {
      alert('Failed to delete conversation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[95vh] sm:max-h-[90vh] flex flex-col border-2 border-[#1086a4]">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-[#1086a4]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="w-6 h-6 text-[#1086a4]" />
              <div>
                <h2 className="text-xl font-bold text-[#054581]">
                  {canManageMembers ? 'Manage Members' : 'View Members'}
                </h2>
                <p className="text-sm text-gray-600">{conversation.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
              disabled={isLoading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {/* Current Participants */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-[#054581] mb-3">
              Current Participants ({conversation.participants?.length || 0})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {conversation.participants?.map(participant => (
                <div key={participant._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <ProfileImage 
                      profilePicture={participant.profilePicture}
                      name={participant.name}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {participant.name} {participant.lastName}
                        </p>
                        {participant._id === conversation.creator?._id && (
                          <Crown className="w-4 h-4 text-yellow-500" title="Creator" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <p className="text-xs text-gray-500 truncate">{participant.email}</p>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeStyle(participant.role)}`}>
                          {participant.role?.charAt(0).toUpperCase() + participant.role?.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {canManageMembers && participant._id !== conversation.creator?._id && (
                    <button
                      onClick={() => handleRemoveParticipant(participant)}
                      className="p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50 transition-colors"
                      disabled={isLoading}
                      title="Remove participant"
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add New Participants */}
          {canManageMembers && (
            <div>
              <h3 className="text-sm font-medium text-[#054581] mb-3">Add New Participants</h3>
              
              {/* Search Box */}
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border border-[#1086a4] rounded-md bg-[#f9fafb] text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              {/* Available Users */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {filteredAvailableUsers.length > 0 ? (
                  filteredAvailableUsers.map(user => (
                    <div key={user._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <ProfileImage 
                          profilePicture={user.profilePicture}
                          name={user.name}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user.name} {user.lastName}
                          </p>
                          <div className="flex items-center space-x-2">
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeStyle(user.role)}`}>
                              {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddParticipant(user)}
                        className="p-2 text-green-500 hover:text-green-700 rounded-full hover:bg-green-50 transition-colors"
                        disabled={isLoading}
                        title="Add participant"
                      >
                        <UserPlus className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">
                      {searchQuery ? 'No matching users found' : 'No available users to add'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {!canManageMembers && conversation.participants?.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No participants in this conversation</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[#1086a4] p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            {canManageMembers && (
              <button
                onClick={handleDeleteConversation}
                className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors order-2 sm:order-1"
                disabled={isLoading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Conversation
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors order-1 sm:order-2"
              disabled={isLoading}
            >
              Close
            </button>
          </div>
        </div>

        {/* Delete Conversation Confirmation */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-red-700 mb-4">Delete Conversation</h3>
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete <strong>"{conversation.name}"</strong>?
              </p>
              <p className="text-gray-600 text-sm mb-6">
                This will permanently delete the entire conversation, all messages, attachments, and history. This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 rounded-lg font-semibold bg-gray-300 hover:bg-gray-400 text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteConversation}
                  className="px-4 py-2 rounded-lg font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remove Participant Confirmation */}
        {showRemoveConfirm && removeUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Remove Participant</h3>
              <p className="text-gray-700 mb-6">
                Are you sure you want to remove <strong>{removeUser.name} {removeUser.lastName}</strong> from this conversation?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowRemoveConfirm(false); setRemoveUser(null); }}
                  className="px-4 py-2 rounded-lg font-semibold bg-gray-300 hover:bg-gray-400 text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRemoveParticipant}
                  className="px-4 py-2 rounded-lg font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationMembersModal;